import { useEffect, useRef } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { FloorPlanResponse } from "./floorApi";

/**
 * The canonical stale-write conflict modal (Tech-decision "Conflict UX on stale save", FL-6, §D.3).
 * A 409 with the authoritative `current` record renders here; the manager chooses to RELOAD their
 * version (discard my draft) or KEEP EDITING and re-apply my changes onto their record (rebase the CAS
 * token, save again). It never auto-merges — two resizes of one table have no correct union. Purely
 * presentational: the screen injects the callbacks (mirrors the landed schedule conflict dialog).
 */
export interface ConflictModalProps {
  current: FloorPlanResponse;
  onReload: () => void;
  onReapply: () => void;
}

export function ConflictModal({ current, onReload, onReapply }: ConflictModalProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  const activeTables = current.tables.filter((tb) => !tb.retired).length;

  return (
    <div
      className={css({
        position: "fixed",
        inset: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bg: "black/40",
        zIndex: "50",
        p: "4",
      })}
    >
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-label={t("floorPlan.conflict.title")}
        tabIndex={-1}
        className={css({
          bg: "bg.default",
          rounded: "lg",
          borderWidth: "1px",
          p: "5",
          width: "full",
          maxW: "md",
          maxH: "85dvh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "3",
          outline: "none",
        })}
      >
        <h2 className={css({ fontSize: "lg", fontWeight: "bold" })}>
          {t("floorPlan.conflict.title")}
        </h2>
        <p className={css({ fontSize: "sm" })}>{t("floorPlan.conflict.message")}</p>
        <p className={css({ fontSize: "sm", color: "fg.muted" })}>
          {t("floorPlan.conflict.current", { version: current.version, tables: activeTables })}
        </p>
        <div
          className={css({
            display: "flex",
            gap: "2",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          })}
        >
          <button
            type="button"
            onClick={onReload}
            className={css({ px: "3", py: "2", rounded: "md", borderWidth: "1px", fontSize: "sm" })}
          >
            {t("floorPlan.conflict.reload")}
          </button>
          <button
            type="button"
            onClick={onReapply}
            className={css({
              px: "3",
              py: "2",
              rounded: "md",
              fontSize: "sm",
              bg: "colorPalette.default",
              color: "colorPalette.fg",
            })}
          >
            {t("floorPlan.conflict.reapply")}
          </button>
        </div>
      </div>
    </div>
  );
}
